import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
    ActionIcon,
    Group,
    ScrollArea,
    Stack,
    Text,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import {
    IconCheck,
    IconChevronDown,
    IconChevronUp,
    IconCopy,
} from '@tabler/icons-react';

import { GET_MANUAL_IMPORT_LOG_ERROR } from '../../apollo/queries/getManualImportLogError';
import { GetManualImportLogErrorQuery } from '../../codegen/graphql';
import { PropagationStopper, When } from '../common';
import { getErrorMessage } from '../../helpers/getErrorMessage';
import {
    showErrorNotification,
    showSuccessNotification,
} from '../../notifications';

type ManualImportErrorCellProps = {
    logId: string;
    status: string;
    error?: string | null;
};

const DISPLAY_LINE_LIMIT = 80;

function formatImportErrors(error: string): string {
    const lines = error
        .split(/,(?=\s*(?:SKU |Duplicate row:|Row \d+:))/)
        .map(line => line.trim())
        .filter(Boolean);

    if (lines.length <= DISPLAY_LINE_LIMIT) {
        return lines.join('\n');
    }

    const remainingCount = lines.length - DISPLAY_LINE_LIMIT;
    return `${lines
        .slice(0, DISPLAY_LINE_LIMIT)
        .join(
            '\n',
        )}\n… and ${remainingCount} more errors. Copy for the full list.`;
}

function getCopyTooltipLabel(
    copied: boolean,
    isLoadingForCopy: boolean,
): string {
    if (copied) {
        return 'Copied';
    }
    if (isLoadingForCopy) {
        return 'Loading errors…';
    }
    return 'Copy error';
}

function ManualImportErrorCell({
    logId,
    status,
    error: providedError,
}: ManualImportErrorCellProps) {
    const [opened, setOpened] = useState(false);
    const [copied, setCopied] = useState(false);
    const [shouldLoadError, setShouldLoadError] = useState(false);
    const isFailed = status === 'Failed';
    const hasProvidedError = providedError != null;
    const shouldQuery =
        isFailed && !hasProvidedError && (opened || shouldLoadError);

    const { data, error, loading, refetch } =
        useQuery<GetManualImportLogErrorQuery>(GET_MANUAL_IMPORT_LOG_ERROR, {
            variables: { id: logId },
            skip: !shouldQuery,
            fetchPolicy: 'cache-first',
        });

    const errorText = providedError ?? data?.getManualImportLogById?.error;
    const formattedError = useMemo(
        () => (errorText ? formatImportErrors(errorText) : ''),
        [errorText],
    );
    const isLoadingForCopy = shouldLoadError && loading && !errorText;

    const copyErrorText = useCallback(async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            showSuccessNotification('Error copied');
            window.setTimeout(() => setCopied(false), 1500);
        } catch (copyError: unknown) {
            showErrorNotification(getErrorMessage(copyError));
        }
    }, []);

    const handleCopy = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();
            if (errorText) {
                await copyErrorText(errorText);
                return;
            }
            if (hasProvidedError) {
                showErrorNotification('No error details were recorded.');
                return;
            }
            if (shouldLoadError) {
                try {
                    const result = await refetch();
                    const fetched = result.data?.getManualImportLogById?.error;
                    if (!fetched) {
                        showErrorNotification(
                            'No error details were recorded.',
                        );
                    }
                } catch (refetchError: unknown) {
                    showErrorNotification(getErrorMessage(refetchError));
                }
                return;
            }
            setShouldLoadError(true);
        },
        [errorText, copyErrorText, hasProvidedError, shouldLoadError, refetch],
    );

    if (!isFailed) {
        return <Text size="sm">-</Text>;
    }

    return (
        <PropagationStopper>
            <Stack gap="xs">
                <Group gap={6} wrap="nowrap">
                    <UnstyledButton
                        onClick={event => {
                            event.stopPropagation();
                            setOpened(current => !current);
                        }}
                        className="flex min-w-0 items-center gap-1 text-xs text-red-600 transition-colors hover:text-red-700"
                    >
                        <When
                            condition={opened}
                            fallback={<IconChevronDown size={14} />}
                        >
                            <IconChevronUp size={14} />
                        </When>
                        <Text
                            component="span"
                            size="xs"
                            className="text-red-600"
                        >
                            View errors
                        </Text>
                    </UnstyledButton>
                    <Tooltip
                        label={getCopyTooltipLabel(copied, isLoadingForCopy)}
                        withArrow
                        openDelay={300}
                    >
                        <ActionIcon
                            variant="transparent"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 shrink-0"
                            onClick={handleCopy}
                            aria-label="Copy error"
                        >
                            {copied ? (
                                <IconCheck size={16} />
                            ) : (
                                <IconCopy size={16} />
                            )}
                        </ActionIcon>
                    </Tooltip>
                </Group>
                <When condition={opened && !hasProvidedError && Boolean(error)}>
                    <Text size="xs" className="text-red-700">
                        {getErrorMessage(error)}
                    </Text>
                </When>
                <When
                    condition={opened && !hasProvidedError && !error && loading}
                >
                    <Text size="xs" className="text-red-700">
                        Loading errors…
                    </Text>
                </When>
                <When
                    condition={
                        opened &&
                        !formattedError &&
                        (hasProvidedError ||
                            (!loading && !error && data !== undefined))
                    }
                >
                    <Text size="xs" className="text-red-700">
                        No error details were recorded.
                    </Text>
                </When>
                <When condition={Boolean(formattedError)}>
                    <div className={opened ? 'block' : 'hidden'}>
                        <ScrollArea
                            type="auto"
                            offsetScrollbars
                            scrollbarSize={8}
                            classNames={{
                                root: 'max-h-40 rounded-[12px] border border-solid border-red-200 bg-red-50',
                                viewport: 'max-h-40 p-3',
                                scrollbar: 'bg-red-100',
                                thumb: 'bg-red-400',
                            }}
                        >
                            <Text
                                component="pre"
                                size="xs"
                                className="m-0 whitespace-pre-wrap break-words text-red-700"
                            >
                                {formattedError}
                            </Text>
                        </ScrollArea>
                    </div>
                </When>
            </Stack>
        </PropagationStopper>
    );
}

export default ManualImportErrorCell;
